import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import FrontrunEventModal, { fmtUsd, fmtPct, pnlClass } from '../components/FrontrunEventModal';

function fmtAddr(a) {
  if (!a) return '—';
  const s = String(a);
  return s.length > 14 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
}

function fmtNum(n, d = 4) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
}

function statusBadge(status) {
  const map = {
    simulated: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    executed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    skipped: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };
  return map[status] || map.skipped;
}

const ALL_NETWORKS = [
  { id: 'base', label: 'Base', native: 'ETH', walletChain: 'evm' },
  { id: 'ethereum', label: 'Ethereum', native: 'ETH', walletChain: 'evm' },
  { id: 'bsc', label: 'BSC', native: 'BNB', walletChain: 'evm' },
  { id: 'robinhood', label: 'Robinhood', native: 'ETH', walletChain: 'robinhood' },
];

function networksForWallet(wallet) {
  if (!wallet) return [];
  if (wallet.chain === 'robinhood') return ALL_NETWORKS.filter((n) => n.id === 'robinhood');
  // EVM monitor scans Base/ETH/BSC/Robinhood
  return ALL_NETWORKS;
}

function targetNetworks(t) {
  if (Array.isArray(t.networks) && t.networks.length) return t.networks;
  if (t.network) return [t.network];
  return [];
}

function walletHasEvm(w) {
  if (!w?.isActive) return false;
  // Manage Wallets / frontrun: admin pool only (never chef user wallets)
  if (w.ownerType === 'user') return false;
  const chains = w.enabledChains || [];
  if (chains.includes('evm')) return true;
  return chains.some((c) => ['base', 'ethereum', 'bsc', 'robinhood'].includes(c))
    || !!(w.chains?.evm || w.chains?.base || w.chains?.ethereum || w.chains?.bsc);
}

export default function Frontrun() {
  const [pageTab, setPageTab] = useState('overview'); // overview | settings
  const [targets, setTargets] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventCounts, setEventCounts] = useState({ all: 0, simulated: 0, skipped: 0 });
  const [eventTab, setEventTab] = useState('simulated');
  const [monitored, setMonitored] = useState([]);
  const [tradingWallets, setTradingWallets] = useState([]);
  const [portfolio, setPortfolio] = useState({ positions: [], summary: null });
  const [analytics, setAnalytics] = useState(null);
  const [sellingId, setSellingId] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalEvent, setModalEvent] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState(null);
  const [form, setForm] = useState({
    monitoredWalletId: '',
    networks: ['base', 'ethereum', 'bsc', 'robinhood'],
    amountNative: '0.05',
    dryRun: true,
    enabled: true,
    label: '',
    minMarketCap: '',
    maxMarketCap: '',
    tokenCooldownMinutes: '60',
    tradingWalletId: '',
  });

  const selectedWallet = useMemo(
    () => monitored.find((w) => String(w._id) === form.monitoredWalletId),
    [monitored, form.monitoredWalletId]
  );
  const editingTarget = useMemo(
    () => (editingId ? targets.find((t) => String(t._id) === String(editingId)) : null),
    [editingId, targets]
  );
  const availableNets = useMemo(() => {
    if (selectedWallet) return networksForWallet(selectedWallet);
    if (editingTarget?.monitorChain === 'robinhood') {
      return ALL_NETWORKS.filter((n) => n.id === 'robinhood');
    }
    if (editingTarget) return ALL_NETWORKS;
    return [];
  }, [selectedWallet, editingTarget]);
  const allAvailableOn = availableNets.length > 0 && availableNets.every((n) => form.networks.includes(n.id));
  const evmTradingWallets = useMemo(
    () => tradingWallets.filter(walletHasEvm),
    [tradingWallets]
  );

  const fetchAll = useCallback(async () => {
    try {
      setError(null);
      const statusQ =
        eventTab === 'all' ? '' : `&status=${encodeURIComponent(eventTab)}`;

      // Fast path first — never block the page on price enrichment
      const [t, e, m, s, tw, pf, an, st] = await Promise.all([
        api.get('/frontrun/targets'),
        api.get(`/frontrun/events?limit=40${statusQ}`),
        api.get('/frontrun/monitored-wallets'),
        api.get('/frontrun/stats'),
        api.get('/trading/wallets'),
        api.get('/frontrun/portfolio'),
        api.get('/frontrun/analytics'),
        api.get('/frontrun/settings'),
      ]);
      setTargets(t.data.targets || []);
      setEvents(e.data.events || []);
      if (e.data.counts) setEventCounts(e.data.counts);
      setMonitored(m.data.wallets || []);
      setStats(s.data.stats || null);
      setTradingWallets(Array.isArray(tw.data) ? tw.data : tw.data?.wallets || []);
      setPortfolio({
        positions: pf.data.positions || [],
        summary: pf.data.summary || null,
      });
      setAnalytics(an.data.analytics || null);
      if (st.data?.settings) {
        setSettings(st.data.settings);
        setSettingsDraft(JSON.parse(JSON.stringify(st.data.settings)));
      }
      setLoading(false);

      // Soft enrich PnL in background (won't hang the UI)
      api
        .get(`/frontrun/events?limit=40&enrich=1${statusQ}`, { timeout: 45000 })
        .then((enriched) => {
          if (enriched.data?.events) setEvents(enriched.data.events);
          if (enriched.data?.counts) setEventCounts(enriched.data.counts);
        })
        .catch(() => {});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
      setLoading(false);
    }
  }, [eventTab]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 45000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      monitoredWalletId: '',
      networks: ['base', 'ethereum', 'bsc', 'robinhood'],
      amountNative: '0.05',
      dryRun: true,
      enabled: true,
      label: '',
      minMarketCap: '',
      maxMarketCap: '',
      tokenCooldownMinutes: '60',
      tradingWalletId: '',
    });
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const selectWallet = (id) => {
    if (editingId) return;
    const w = monitored.find((x) => String(x._id) === id);
    const nets = networksForWallet(w).map((n) => n.id);
    setForm({
      ...form,
      monitoredWalletId: id,
      networks: nets,
      label: form.label || w?.label || '',
    });
  };

  const startEdit = (target) => {
    setEditingId(target._id);
    setForm({
      monitoredWalletId: target.monitoredWalletId ? String(target.monitoredWalletId) : '',
      networks: targetNetworks(target),
      amountNative: target.amountNative != null ? String(target.amountNative) : '0.05',
      dryRun: target.dryRun !== false,
      enabled: target.enabled !== false,
      label: target.label || '',
      minMarketCap: target.minMarketCap ? String(target.minMarketCap) : '',
      maxMarketCap: target.maxMarketCap ? String(target.maxMarketCap) : '',
      tokenCooldownMinutes:
        target.tokenCooldownMinutes != null ? String(target.tokenCooldownMinutes) : '60',
      tradingWalletId: target.tradingWalletId ? String(target.tradingWalletId) : '',
    });
    setShowForm(true);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleFormNetwork = (netId) => {
    const on = form.networks.includes(netId);
    const next = on ? form.networks.filter((n) => n !== netId) : [...form.networks, netId];
    setForm({ ...form, networks: next });
  };

  const toggleAllFormNetworks = () => {
    if (allAvailableOn) {
      setForm({ ...form, networks: [] });
    } else {
      setForm({ ...form, networks: availableNets.map((n) => n.id) });
    }
  };

  const handleSaveTarget = async (ev) => {
    ev.preventDefault();
    if (!editingId && !form.monitoredWalletId) {
      setError('Select a monitored wallet');
      return;
    }
    if (!form.networks.length) {
      setError('Enable at least one network');
      return;
    }
    if (!form.dryRun && !form.tradingWalletId) {
      setError('Select a trading wallet for live mode (from Manage Wallets)');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const mw = monitored.find((w) => String(w._id) === form.monitoredWalletId);
      const payload = {
        networks: form.networks,
        amountNative: Number(form.amountNative),
        dryRun: form.dryRun,
        enabled: form.enabled,
        label: form.label || mw?.label || undefined,
        minMarketCap: form.minMarketCap ? Number(form.minMarketCap) : 0,
        maxMarketCap: form.maxMarketCap ? Number(form.maxMarketCap) : 0,
        tokenCooldownMinutes: Number(form.tokenCooldownMinutes) || 60,
        tradingWalletId: form.tradingWalletId || null,
      };
      if (editingId) {
        await api.put(`/frontrun/targets/${editingId}`, payload);
      } else {
        await api.post('/frontrun/targets', {
          ...payload,
          monitoredWalletId: form.monitoredWalletId,
        });
      }
      closeForm();
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || err.message || (editingId ? 'Failed to update' : 'Failed to add'));
    } finally {
      setSaving(false);
    }
  };

  const openEventModal = async (ev) => {
    setModalEvent(ev);
    setModalLoading(true);
    try {
      const res = await api.get(`/frontrun/events/${ev._id}`);
      setModalEvent(res.data.event);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load event');
    } finally {
      setModalLoading(false);
    }
  };

  const toggleEnabled = async (target) => {
    try {
      await api.put(`/frontrun/targets/${target._id}`, { enabled: !target.enabled });
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Toggle failed');
    }
  };

  const toggleTargetNetwork = async (target, netId) => {
    const current = targetNetworks(target);
    const allowed = target.monitorChain === 'robinhood'
      ? ['robinhood']
      : ['base', 'ethereum', 'bsc', 'robinhood'];
    if (!allowed.includes(netId)) return;
    const on = current.includes(netId);
    const next = on ? current.filter((n) => n !== netId) : [...current, netId];
    if (!next.length) {
      setError('Keep at least one network enabled');
      return;
    }
    try {
      setError(null);
      await api.put(`/frontrun/targets/${target._id}`, { networks: next });
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Network update failed');
    }
  };

  const sellPosition = async (position, percent) => {
    const sym = position.tokenSymbol || fmtAddr(position.tokenAddress);
    if (
      !window.confirm(
        `Sell ${percent}% of remaining ${sym} on ${position.chain}? This spends gas from the trading wallet.`
      )
    ) {
      return;
    }
    setSellingId(`${position._id}:${percent}`);
    setError(null);
    try {
      await api.post(`/frontrun/events/${position._id}/sell`, { percent });
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Sell failed');
    } finally {
      setSellingId(null);
    }
  };

  const saveSettings = async () => {
    if (!settingsDraft) return;
    setSettingsSaving(true);
    setSettingsMsg(null);
    setError(null);
    try {
      const res = await api.put('/frontrun/settings', { settings: settingsDraft });
      const next = res.data.settings || settingsDraft;
      setSettings(next);
      setSettingsDraft(JSON.parse(JSON.stringify(next)));
      setSettingsMsg('Settings saved. Worker picks up exit rules on the next poll tick; restart cookbot-worker to apply poll interval.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const updateTier = (idx, field, value) => {
    setSettingsDraft((prev) => {
      if (!prev) return prev;
      const tiers = [...(prev.exit?.tiers || [])];
      tiers[idx] = { ...tiers[idx], [field]: value };
      return { ...prev, exit: { ...prev.exit, tiers } };
    });
  };

  const removeTarget = async (target) => {
    if (!window.confirm(`Remove frontrun for ${target.label || fmtAddr(target.walletAddress)}?`)) return;
    try {
      await api.delete(`/frontrun/targets/${target._id}`);
      await fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  if (loading && targets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading frontrun…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Frontrun</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Copy buys when a monitored wallet buys — dry-run or live via Manage Wallets.
          </p>
          <div className="mt-3 flex gap-1">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'settings', label: 'Settings' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPageTab(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  pageTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {pageTab === 'overview' && (
          <button
            type="button"
            onClick={() => {
              if (showForm) closeForm();
              else {
                resetForm();
                setShowForm(true);
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {showForm ? 'Cancel' : '+ Add'}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {pageTab === 'settings' && !settingsDraft && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-sm text-gray-500">
          Loading settings…
        </div>
      )}

      {pageTab === 'settings' && settingsDraft && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6 max-w-3xl">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Auto exit rules</h2>
            <p className="text-sm text-gray-500 mt-1">
              TP sells % of remaining. Moonbag keeps that % of original bag. Trailing arms after activation multiple, then exits on drop from peak.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!settingsDraft.exit?.autoExitEnabled}
              onChange={(e) =>
                setSettingsDraft((p) => ({
                  ...p,
                  exit: { ...p.exit, autoExitEnabled: e.target.checked },
                }))
              }
            />
            Enable auto take-profit + trailing stop
          </label>
          <div className="space-y-3">
            <div className="text-sm font-medium">Take-profit tiers</div>
            {(settingsDraft.exit?.tiers || []).map((tier, idx) => (
              <div key={tier.id || idx} className="grid grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{tier.label || `TP${idx + 1}`} multiple</label>
                  <input
                    type="number"
                    min="1.01"
                    step="0.1"
                    value={tier.multiple}
                    onChange={(e) => updateTier(idx, 'multiple', e.target.value)}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Sell % of remaining</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={tier.sellPercent}
                    onChange={(e) => updateTier(idx, 'sellPercent', e.target.value)}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  />
                </div>
                <div className="text-xs text-gray-500 pb-2">
                  {tier.multiple}x → {tier.sellPercent}%
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Moonbag % of original</label>
              <input
                type="number"
                min="0"
                max="99"
                value={settingsDraft.exit?.moonbagPercent ?? 20}
                onChange={(e) =>
                  setSettingsDraft((p) => ({
                    ...p,
                    exit: { ...p.exit, moonbagPercent: e.target.value },
                  }))
                }
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max slippage %</label>
              <input
                type="number"
                min="0.5"
                max="50"
                step="0.5"
                value={settingsDraft.monitoring?.maxSlippagePercent ?? 5}
                onChange={(e) =>
                  setSettingsDraft((p) => ({
                    ...p,
                    monitoring: { ...p.monitoring, maxSlippagePercent: e.target.value },
                  }))
                }
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={settingsDraft.exit?.trailingStopEnabled !== false}
                onChange={(e) =>
                  setSettingsDraft((p) => ({
                    ...p,
                    exit: { ...p.exit, trailingStopEnabled: e.target.checked },
                  }))
                }
              />
              Enable trailing stop
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Arm after multiple</label>
                <input
                  type="number"
                  min="1.01"
                  step="0.1"
                  value={settingsDraft.exit?.trailingStopActivationMultiple ?? 5}
                  onChange={(e) =>
                    setSettingsDraft((p) => ({
                      ...p,
                      exit: { ...p.exit, trailingStopActivationMultiple: e.target.value },
                    }))
                  }
                  className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Exit on drop from peak %</label>
                <input
                  type="number"
                  min="5"
                  max="95"
                  value={settingsDraft.exit?.trailingStopDropPercent ?? 50}
                  onChange={(e) =>
                    setSettingsDraft((p) => ({
                      ...p,
                      exit: { ...p.exit, trailingStopDropPercent: e.target.value },
                    }))
                  }
                  className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Position poll interval (ms)</label>
            <input
              type="number"
              min="15000"
              max="300000"
              step="1000"
              value={settingsDraft.monitoring?.positionPollMs ?? 45000}
              onChange={(e) =>
                setSettingsDraft((p) => ({
                  ...p,
                  monitoring: { ...p.monitoring, positionPollMs: e.target.value },
                }))
              }
              className="w-full max-w-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Restart cookbot-worker to apply a new poll interval.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={settingsSaving}
              onClick={saveSettings}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {settingsSaving ? 'Saving…' : 'Save settings'}
            </button>
            <button
              type="button"
              onClick={() => settings && setSettingsDraft(JSON.parse(JSON.stringify(settings)))}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
            >
              Reset
            </button>
            {settingsMsg && <span className="text-sm text-green-600">{settingsMsg}</span>}
          </div>
        </div>
      )}

      {pageTab === 'overview' && (
      <>
      {analytics && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-gray-900 dark:text-white">Live analytics</h2>
            <span
              className={`text-xs px-2 py-1 rounded-full ${
                analytics.liveReady?.status === 'live_active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                  : analytics.liveReady?.status === 'live_funded_waiting'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                    : analytics.liveReady?.status === 'live_unfunded'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-600'
              }`}
            >
              {analytics.liveReady?.status === 'live_active'
                ? 'Live active'
                : analytics.liveReady?.status === 'live_funded_waiting'
                  ? 'Live ready — waiting for next buy'
                  : analytics.liveReady?.status === 'live_unfunded'
                    ? 'Live targets but wallet unfunded'
                    : 'No live targets'}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3">
              <div className="text-xs text-gray-500">Funded (ETH)</div>
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                {fmtNum(analytics.funded?.eth, 4)}
              </div>
              <div className="text-xs text-gray-500">{fmtUsd(analytics.funded?.usd)}</div>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3">
              <div className="text-xs text-gray-500">Cost spent</div>
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                {fmtNum(analytics.cost?.eth, 4)} ETH
              </div>
              <div className="text-xs text-gray-500">
                {fmtUsd(analytics.cost?.usd)} · {analytics.cost?.fills || 0} fills
                {analytics.cost?.nativeBnb > 0 ? ` · ${fmtNum(analytics.cost.nativeBnb, 4)} BNB` : ''}
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3">
              <div className="text-xs text-gray-500">Portfolio</div>
              <div className="text-xl font-semibold text-gray-900 dark:text-white">
                {fmtNum(analytics.portfolio?.eth, 4)} ETH
              </div>
              <div className="text-xs text-gray-500">{fmtUsd(analytics.portfolio?.usd)}</div>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3">
              <div className="text-xs text-gray-500">PnL</div>
              <div className={`text-xl font-semibold ${pnlClass(analytics.portfolio?.pnlUsd)}`}>
                {fmtUsd(analytics.portfolio?.pnlUsd)}
              </div>
              <div className="text-xs text-gray-500">
                ETH ≈ {fmtNum(analytics.ethUsd, 0)} · {analytics.liveReady?.liveTargets || 0} live targets
              </div>
            </div>
          </div>
          {analytics.funded?.wallets?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="text-left text-gray-500">
                  <tr>
                    <th className="py-1 pr-3">Wallet</th>
                    <th className="py-1 pr-3">RH ETH</th>
                    <th className="py-1 pr-3">Base</th>
                    <th className="py-1 pr-3">ETH</th>
                    <th className="py-1 pr-3">BSC</th>
                    <th className="py-1">Funded ETH</th>
                  </tr>
                </thead>
                <tbody className="text-gray-900 dark:text-gray-100">
                  {analytics.funded.wallets.map((w) => (
                    <tr key={w.walletId} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-1.5 pr-3">
                        <div className="font-medium">{w.label}</div>
                        <div className="font-mono text-gray-500">{fmtAddr(w.address)}</div>
                      </td>
                      <td className="py-1.5 pr-3">{fmtNum(w.balances?.robinhood?.native, 4)}</td>
                      <td className="py-1.5 pr-3">{fmtNum(w.balances?.base?.native, 4)}</td>
                      <td className="py-1.5 pr-3">{fmtNum(w.balances?.ethereum?.native, 4)}</td>
                      <td className="py-1.5 pr-3">{fmtNum(w.balances?.bsc?.native, 4)} BNB</td>
                      <td className="py-1.5 font-medium">{fmtNum(w.fundedEth, 4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Enabled</div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.enabledTargets}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Live targets</div>
            <div className="text-2xl font-semibold text-red-600">{stats.liveTargets ?? 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Events (24h)</div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.events24h}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Dry-runs (24h)</div>
            <div className="text-2xl font-semibold text-amber-600 dark:text-amber-400">{stats.simulated24h}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Executed (24h)</div>
            <div className="text-2xl font-semibold text-green-600">{stats.executed24h ?? 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Failed live (24h)</div>
            <div className="text-2xl font-semibold text-red-600">{stats.failed24h ?? 0}</div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            {editingId ? 'Edit frontrun target' : 'Add frontrun target'}
          </h3>
          {monitored.length === 0 && !editingId ? (
            <p className="text-sm text-gray-500">
              No active EVM / Robinhood wallets in Wallet Monitor. Add one there first.
            </p>
          ) : (
            <form onSubmit={handleSaveTarget} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Monitored wallet *
                  </label>
                  <select
                    value={form.monitoredWalletId}
                    onChange={(e) => selectWallet(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600 disabled:opacity-60"
                    required={!editingId}
                    disabled={!!editingId}
                  >
                    <option value="">Select wallet…</option>
                    {monitored.map((w) => (
                      <option key={w._id} value={w._id}>
                        {(w.label || 'Wallet')} · {w.chain} · {fmtAddr(w.walletAddress)}
                      </option>
                    ))}
                  </select>
                  {editingId && (
                    <p className="text-xs text-gray-500 mt-1">Wallet can’t be changed — remove and re-add to switch.</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Networks *
                    </label>
                    {availableNets.length > 1 && (
                      <button
                        type="button"
                        onClick={toggleAllFormNetworks}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        disabled={!form.monitoredWalletId}
                      >
                        {allAvailableOn ? 'Disable all' : 'Enable all'}
                      </button>
                    )}
                  </div>
                  {!form.monitoredWalletId ? (
                    <p className="text-xs text-gray-500">Select a wallet to choose networks.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {availableNets.map((n) => {
                        const on = form.networks.includes(n.id);
                        return (
                          <button
                            key={n.id}
                            type="button"
                            onClick={() => toggleFormNetwork(n.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                              on
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            {n.label}
                            <span className="opacity-70 ml-1 text-xs">({n.native})</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Toggle any mix — fires on enabled networks. EVM wallets can enable Base / Ethereum / BSC / Robinhood.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Trading wallet {!form.dryRun && <span className="text-red-500">*</span>}
                    </label>
                    <Link
                      to="/trading/wallets"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Manage wallets
                    </Link>
                  </div>
                  <select
                    value={form.tradingWalletId}
                    onChange={(e) => setForm({ ...form, tradingWalletId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                    required={!form.dryRun}
                  >
                    <option value="">
                      {form.dryRun ? 'Optional for dry run…' : 'Select trading wallet…'}
                    </option>
                    {evmTradingWallets.map((w) => {
                      const addr =
                        w.chains?.evm?.address ||
                        w.chains?.base?.address ||
                        w.chains?.ethereum?.address ||
                        w.chains?.bsc?.address;
                      return (
                        <option key={w._id} value={w._id}>
                          {w.label}
                          {addr ? ` · ${fmtAddr(addr)}` : ''}
                        </option>
                      );
                    })}
                  </select>
                  {evmTradingWallets.length === 0 ? (
                    <p className="text-xs text-amber-600 mt-1">
                      No EVM trading wallets yet.{' '}
                      <Link to="/trading/wallets" className="underline">
                        Create one in Manage Wallets
                      </Link>{' '}
                      (enable Base / Ethereum / BSC).
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Live buys spend native from this wallet. Required when Dry run is off.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Amount (native) *
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0.0001"
                    value={form.amountNative}
                    onChange={(e) => setForm({ ...form, amountNative: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">ETH on Base / Ethereum / Robinhood · BNB on BSC</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Label
                  </label>
                  <input
                    type="text"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Min MC (optional)
                  </label>
                  <input
                    type="number"
                    value={form.minMarketCap}
                    onChange={(e) => setForm({ ...form, minMarketCap: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Max MC (optional)
                  </label>
                  <input
                    type="number"
                    value={form.maxMarketCap}
                    onChange={(e) => setForm({ ...form, maxMarketCap: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Token cooldown (min)
                  </label>
                  <input
                    type="number"
                    value={form.tokenCooldownMinutes}
                    onChange={(e) => setForm({ ...form, tokenCooldownMinutes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                  />
                </div>
                <div className="flex flex-col gap-3 pt-2 md:pt-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={form.dryRun}
                      onChange={(e) => setForm({ ...form, dryRun: e.target.checked })}
                    />
                    Dry run only
                  </label>
                  {!form.dryRun && (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Live mode will spend real funds from the trading wallet on each trigger.
                    </p>
                  )}
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    />
                    Enabled
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={saving || !form.networks.length || (!form.dryRun && !form.tradingWalletId)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingId ? 'Save target' : 'Add target'}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={closeForm}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:underline"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 font-semibold text-gray-900 dark:text-white">
          Targets
        </div>
        {targets.length === 0 ? (
          <div className="p-6 text-gray-500 text-sm">No frontrun targets yet. Click + Add.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-2">Wallet</th>
                  <th className="px-4 py-2">Networks</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Mode</th>
                  <th className="px-4 py-2">Trade wallet</th>
                  <th className="px-4 py-2">Stats</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {targets.map((t) => {
                  const nets = targetNetworks(t);
                  const allowed =
                    t.monitorChain === 'robinhood'
                      ? ALL_NETWORKS.filter((n) => n.id === 'robinhood')
                      : ALL_NETWORKS;
                  return (
                    <tr key={t._id} className="text-gray-900 dark:text-gray-100">
                      <td className="px-4 py-3">
                        <div className="font-medium">{t.label || '—'}</div>
                        <div className="text-xs text-gray-500 font-mono">{fmtAddr(t.walletAddress)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {allowed.map((n) => {
                            const on = nets.includes(n.id);
                            return (
                              <button
                                key={n.id}
                                type="button"
                                title={on ? `Disable ${n.label}` : `Enable ${n.label}`}
                                onClick={() => toggleTargetNetwork(t, n.id)}
                                className={`px-2 py-0.5 rounded text-xs border ${
                                  on
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-transparent text-gray-500 border-gray-300 dark:border-gray-600'
                                }`}
                              >
                                {n.label}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3">{fmtNum(t.amountNative, 6)}</td>
                      <td className="px-4 py-3">
                        {t.dryRun !== false ? (
                          <span className="text-amber-600 dark:text-amber-400">Dry run</span>
                        ) : (
                          <span className="text-red-600 font-medium">Live</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {t.tradingWalletLabel || (t.tradingWalletId ? fmtAddr(String(t.tradingWalletId)) : (
                          <span className="text-gray-400">—</span>
                        ))}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {t.stats?.dryRuns || 0} dry · {t.stats?.lives || 0} live · {t.stats?.skipped || 0} skip
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleEnabled(t)}
                          className={`px-2 py-1 rounded text-xs ${
                            t.enabled
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700'
                          }`}
                        >
                          {t.enabled ? 'On' : 'Off'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => startEdit(t)}
                          className="text-blue-600 hover:underline text-xs mr-3"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removeTarget(t)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">Portfolio</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Live positions · TP 2x/5x/10x (50% each) · 20% moonbag · trail after 5x / −50% · manual 25/50/70/100%
            </div>
          </div>
          {portfolio.summary && portfolio.summary.count > 0 && (
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-gray-500 text-xs">Positions</span>
                <div className="font-medium text-gray-900 dark:text-white">{portfolio.summary.count}</div>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Cost</span>
                <div className="font-medium text-gray-900 dark:text-white">{fmtUsd(portfolio.summary.costUsd)}</div>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Live bag</span>
                <div className="font-medium text-gray-900 dark:text-white">{fmtUsd(portfolio.summary.liveUsd)}</div>
              </div>
              {portfolio.summary.realizedUsd != null && portfolio.summary.realizedUsd > 0 && (
                <div>
                  <span className="text-gray-500 text-xs">Realized TP</span>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {fmtUsd(portfolio.summary.realizedUsd)}
                  </div>
                </div>
              )}
              <div>
                <span className="text-gray-500 text-xs">PnL</span>
                <div className={`font-semibold ${pnlClass(portfolio.summary.pnlPct)}`}>
                  {fmtPct(portfolio.summary.pnlPct)}{' '}
                  <span className="text-xs font-normal">({fmtUsd(portfolio.summary.pnlUsd)})</span>
                </div>
              </div>
            </div>
          )}
        </div>
        {!portfolio.positions?.length ? (
          <div className="p-6 text-gray-500 text-sm">
            No live positions yet. Turn off Dry run on a target, attach a trading wallet, and executed buys will show here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-2">Token</th>
                  <th className="px-4 py-2">Chain</th>
                  <th className="px-4 py-2">Wallet</th>
                  <th className="px-4 py-2">Entry</th>
                  <th className="px-4 py-2">Live</th>
                  <th className="px-4 py-2">PnL</th>
                  <th className="px-4 py-2">Sell</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {portfolio.positions.map((p) => {
                  const pnl = p.pnl || {};
                  const busy = sellingId && String(sellingId).startsWith(String(p._id));
                  return (
                    <tr
                      key={p._id}
                      className="text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                      onClick={() => openEventModal(p)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{p.tokenSymbol || '—'}</div>
                        <div className="text-xs text-gray-500 font-mono">{fmtAddr(p.tokenAddress)}</div>
                        {Array.isArray(p.tpTiersHit) && p.tpTiersHit.length > 0 && (
                          <div className="text-xs text-green-600 mt-0.5">
                            TP hit: {p.tpTiersHit.join(', ')}
                          </div>
                        )}
                        {p.trailingArmed && (
                          <div className="text-xs text-amber-600 mt-0.5">
                            Trail armed
                            {p.peakMultiple != null ? ` · peak ${Number(p.peakMultiple).toFixed(2)}x` : ''}
                          </div>
                        )}
                        {p.autoTp2xDone && !(p.tpTiersHit?.length > 0) && (
                          <div className="text-xs text-green-600 mt-0.5">Auto TP done</div>
                        )}
                        {p.percentSold > 0 && (
                          <div className="text-xs text-gray-500">Sold {fmtNum(p.percentSold, 0)}%</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs uppercase">{p.chain}</td>
                      <td className="px-4 py-3 text-xs">{p.tradingWalletLabel || '—'}</td>
                      <td className="px-4 py-3">
                        <div>{fmtUsd(pnl.entryUsd)}</div>
                        <div className="text-xs text-gray-500">{fmtUsd(pnl.entryMarketCap ?? p.marketCap)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{fmtUsd(pnl.liveUsd)}</div>
                        <div className="text-xs text-gray-500">{fmtUsd(pnl.liveMarketCap)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`font-semibold ${pnlClass(pnl.pnlPct)}`}>{fmtPct(pnl.pnlPct)}</div>
                        <div className={`text-xs ${pnlClass(pnl.pnlUsd)}`}>{fmtUsd(pnl.pnlUsd)}</div>
                        {pnl.realizedUsd != null && Number(pnl.realizedUsd) > 0 && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            TP {fmtUsd(pnl.realizedUsd)}
                            {pnl.liveUsd != null ? ` · bag ${fmtUsd(pnl.liveUsd)}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap gap-1">
                          {[25, 50, 70, 100].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              disabled={!!busy}
                              onClick={() => sellPosition(p, pct)}
                              className={`px-2 py-1 rounded text-xs border disabled:opacity-50 ${
                                pct === 100
                                  ? 'border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {sellingId === `${p._id}:${pct}` ? '…' : `${pct}%`}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
          <div className="font-semibold text-gray-900 dark:text-white">Events</div>
          <div className="flex items-center gap-1 text-sm">
            {[
              { id: 'simulated', label: 'Simulated', count: eventCounts.simulated },
              { id: 'skipped', label: 'Skipped', count: eventCounts.skipped },
              { id: 'all', label: 'All', count: eventCounts.all },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setEventTab(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  eventTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 ${eventTab === tab.id ? 'opacity-80' : 'opacity-50'}`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>
        {events.length === 0 ? (
          <div className="p-6 text-gray-500 text-sm">
            {eventTab === 'skipped'
              ? 'No skipped events.'
              : eventTab === 'simulated'
                ? 'No simulated entries yet.'
                : 'No events yet. When a target wallet buys on an enabled network, a dry-run row will appear here.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-2">Time</th>
                  <th className="px-4 py-2">Token</th>
                  <th className="px-4 py-2">Chain</th>
                  <th className="px-4 py-2">Entry MC</th>
                  <th className="px-4 py-2">PnL</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {events.map((ev) => {
                  const pnl = ev.pnl || {};
                  return (
                    <tr
                      key={ev._id}
                      className="text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                      onClick={() => openEventModal(ev)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {ev.createdAt ? new Date(ev.createdAt).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{ev.tokenSymbol || '—'}</div>
                        <div className="text-xs text-gray-500 font-mono">{fmtAddr(ev.tokenAddress)}</div>
                      </td>
                      <td className="px-4 py-3 text-xs uppercase">{ev.chain}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{fmtUsd(pnl.entryMarketCap ?? ev.marketCap)}</div>
                        <div className="text-xs text-gray-500">{fmtUsd(pnl.entryPriceUsd ?? ev.priceUsd, 6)}</div>
                      </td>
                      <td className="px-4 py-3">
                        {ev.status === 'simulated' || ev.status === 'executed' ? (
                          <>
                            <div className={`font-semibold ${pnlClass(pnl.pnlPct)}`}>{fmtPct(pnl.pnlPct)}</div>
                            <div className={`text-xs ${pnlClass(pnl.pnlUsd)}`}>{fmtUsd(pnl.pnlUsd)}</div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${statusBadge(ev.status)}`}>
                          {ev.status}
                        </span>
                        {ev.skipReason && (
                          <div className="text-xs text-gray-500 mt-1 max-w-[180px] truncate" title={ev.skipReason}>
                            {ev.skipReason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <Link
                          to={`/frontrun/events/${ev._id}`}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </>
      )}

      <FrontrunEventModal
        isOpen={!!modalEvent}
        onClose={() => setModalEvent(null)}
        event={modalEvent}
        loading={modalLoading}
      />
    </div>
  );
}
